import {
  Box,
  List,
  ListItem,
  ListItemProps,
  ListProps,
  styled
} from '@mui/material';
import React, { forwardRef, useEffect, useMemo, useState } from 'react';
import {
  Components,
  Virtuoso,
  VirtuosoHandle,
  VirtuosoProps
} from 'react-virtuoso';

// Styled container to match MUI List styling
const VirtuosoContainer = styled('div')(({ theme }) => ({
  height: '100%',
  width: '100%',
  outline: 'none',
}));

// Styled scroller to match MUI List styling
const VirtuosoScroller = styled('div')({
  height: '100%',
  width: '100%',
  overflow: 'auto',
});

export interface VirtualizedListProps<T> extends Omit<VirtuosoProps<T, unknown>, 'components'> {
  /**
   * List component props
   */
  ListProps?: Omit<ListProps, 'component'>;

  /**
   * Default props for each list item
   */
  ListItemProps?: Omit<ListItemProps, 'component'>;

  /**
   * Function to render each list item
   */
  renderItem: (item: T, index: number) => React.ReactNode;

  /**
   * Optional header element
   */
  header?: React.ReactNode;

  /**
   * Optional footer element
   */
  footer?: React.ReactNode;

  /**
   * Optional empty list component
   */
  emptyComponent?: React.ReactNode;

  /**
   * The data items to be rendered in the list
   */
  data: T[];

  /**
   * Optional initial index to scroll to
   */
  initialTopMostItemIndex?: number;

  /**
   * Custom components to override Virtuoso defaults
   */
  customComponents?: Partial<Components>;

  /**
   * Whether to automatically adjust the height based on content
   * When true, if content height is less than the provided height,
   * the component will shrink to fit content
   */
  autoAdjustHeight?: boolean;
}

const CustomList = (ListProps: ListProps) => {
  const _CustomList = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>((listProps, listRef) => (
    <List
      component="div"
      ref={listRef}
      {...ListProps}
      {...listProps}
    />
  ))
  _CustomList.displayName = 'CustomList';
  return _CustomList;
}

const CustomListItem = (ListItemProps: ListItemProps) => {
  const _CustomListItem = forwardRef<HTMLLIElement, React.HTMLAttributes<HTMLLIElement>>((itemProps, itemRef) => (
    <ListItem
      component="div"
      ref={itemRef}
      sx={{ padding: 0 }}
      {...ListItemProps}
      {...itemProps}
    />
  ))
  _CustomListItem.displayName = 'CustomListItem';
  return _CustomListItem;
}

const CustomScroller = forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLDivElement>>((scrollerProps, scrollerRef) => (
  <VirtuosoScroller {...scrollerProps} ref={scrollerRef} />
))

CustomScroller.displayName = 'CustomScroller';

/**
 * VirtualizedList component that integrates react-virtuoso with Material-UI List
 * 
 * @example
 * ```tsx
 * <VirtualizedList
 *   data={items}
 *   renderItem={(item, index) => (
 *     <ListItem>
 *       <ListItemText primary={item.title} />
 *     </ListItem>
 *   )}
 *   height={400}
 *   ListProps={{ dense: true }}
 * />
 * ```
 */
export const VirtualizedList = forwardRef(<T,>(
  props: VirtualizedListProps<T>,
  ref: React.ForwardedRef<VirtuosoHandle>
) => {
  const {
    data,
    renderItem,
    header,
    height,
    footer,
    emptyComponent,
    ListProps,
    ListItemProps,
    customComponents,
    autoAdjustHeight = true,
    ...virtuosoProps
  } = props;

  const [actualHeight, setActualHeight] = useState<number | string>(height || '100%');
  const [contentHeight, setContentHeight] = useState<number>(0);

  useEffect(() => {
    if (autoAdjustHeight && typeof height === 'number' && contentHeight > 0) {
      setActualHeight(Math.min(height, contentHeight));
    } else {
      setActualHeight(height || '100%');
    }
  }, [height, contentHeight, autoAdjustHeight]);

  const components = useMemo(() => ({
    List: CustomList(ListProps || {}),
    Item: CustomListItem(ListItemProps || {}),
    Scroller: CustomScroller,
    EmptyPlaceholder: () => <>{emptyComponent}</>,
    Header: () => <>{header}</>,
    Footer: () => <>{footer}</>,
    ...customComponents,
  }), [ListProps, ListItemProps, customComponents, emptyComponent, header, footer]);

  // Show empty component when there's no data
  if (data.length === 0 && emptyComponent) {
    return <>{emptyComponent}</>;
  }

  return (
    <Box height={actualHeight}>
      <VirtuosoContainer>
        <Virtuoso
          ref={ref}
          data={data}
          itemContent={(index, item) => renderItem(item, index)}
          components={components as Components<T, unknown>}
          totalListHeightChanged={setContentHeight}
          {...virtuosoProps}
        />
      </VirtuosoContainer>
    </Box>
  );
});

VirtualizedList.displayName = 'VirtualizedList';

export default VirtualizedList; 